// Connexion et création d'organisation.
//
// La page d'accueil est la présentation (`public/decouvrir/`) ; cet écran vit à
// /connexion et /inscription (et sur toute adresse profonde sans session) :
// une courte présentation à gauche, le formulaire à droite. Créer un compte y est une
// DEMANDE — l'exploitant l'approuve depuis le back-office avant toute connexion.
//
// Les deux vivent dans le même écran : c'est une bascule, pas une navigation —
// on ne perd pas ce qui a été saisi, et l'utilisateur voit tout de suite que
// l'autre option existe. L'ancien frontend proposait ici un faux formulaire de
// contact (« Demander un accès ») qui n'appelait aucune route : la création de
// compte était impossible depuis l'application déployée.
import { useState, type FormEvent } from 'react';
import { useLogin, useRegister } from '../api/queries';
import { ApiRequestError } from '../api/client';
import { Button, ErrorBanner, Field } from '../components/ui';
import { LogoMark, Wordmark } from '../components/Logo';
import { LegalFooter } from '../legal/LegalPages';
import { TermsConsent } from '../legal/TermsConsent';
import { Icon, type IconName } from '../components/icons';
import { ResearchIllustration } from '../portfolio/Illustration';
import type { Hue } from '../taxonomy/colors';
import '../portfolio/portfolio.css';
import './auth.css';

/** Ce que fait le produit, en trois lignes — pas une plaquette. */
const POINTS: { icon: IconName; hue: Hue; title: string; text: string }[] = [
  { icon: 'gauge', hue: 'green', title: 'Un baromètre régulier', text: 'NPS, SUS et piliers adaptés à votre modèle, comparables d’une vague à l’autre.' },
  { icon: 'message', hue: 'orange', title: 'Des explications tracées', text: 'Études et verbatims disent pourquoi un chiffre bouge, et chaque citation garde sa source.' },
  { icon: 'bulb', hue: 'amber', title: 'Des décisions prouvées', text: 'Chaque insight cite ses preuves, et chaque correction se juge à la mesure suivante.' },
];

// « demo » : l'inscription de la bêta, ouverte tout de suite avec un projet exemple.
type Mode = 'login' | 'register' | 'demo';

/** Nom donné à l'espace d'un visiteur de la démo qui n'en a pas saisi. */
const DEMO_ORG_NAME = 'Mon espace Zygosight';

/** Le serveur impose 8 caractères ; on le dit AVANT l'envoi plutôt qu'après. */
const MIN_PASSWORD = 8;

export function AuthPage({ initialMode = 'login' }: { initialMode?: Mode }) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  // La demande de compte est partie : on attend l'approbation de l'exploitant.
  const [requested, setRequested] = useState(false);

  const login = useLogin();
  const register = useRegister();
  const busy = login.isPending || register.isPending;

  const isDemo = mode === 'demo';
  const isRegister = mode !== 'login';
  const passwordTooShort = isRegister && password.length > 0 && password.length < MIN_PASSWORD;

  const canSubmit = isRegister
    ? Boolean(email && name && (organizationName || isDemo)) && password.length >= MIN_PASSWORD && acceptTerms
    : Boolean(email && password);

  const switchMode = (next: Mode) => {
    setMode(next);
    setError('');
  };

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit || busy) return;
    setError('');
    try {
      // La mutation recharge l'identité : la racine bascule d'elle-même vers
      // l'application dès que le cache est à jour.
      if (isRegister) {
        const res = await register.mutateAsync({
          email, password, name, acceptTerms,
          organizationName: organizationName.trim() || (isDemo ? DEMO_ORG_NAME : organizationName),
          ...(isDemo ? { demo: true } : {}),
        });
        // Compte créé mais pas encore ouvert : il attend l'approbation.
        if (res.pending) {
          setRequested(true);
          setPassword('');
        }
      } else {
        await login.mutateAsync({ email, password });
      }
    } catch (cause) {
      // On préfère le message du serveur quand il existe : lui seul sait
      // distinguer « e-mail déjà pris » d'« identifiants invalides ».
      if (cause instanceof ApiRequestError) {
        setError(
          cause.status === 409
            ? 'Cet e-mail est déjà utilisé par un compte existant.'
            : cause.message,
        );
      } else {
        setError('Une erreur inattendue est survenue.');
      }
    }
  }

  return (
    <div className="auth-page">
    <main className="auth">
      {/* À GAUCHE, ce qu'est le produit — en trois lignes, pas une plaquette.
          À DROITE, ce pour quoi l'on vient : se connecter ou demander un compte. */}
      <section className="auth-pitch" aria-labelledby="auth-pitch-title">
        <div className="auth-brand">
          <LogoMark size={32} />
          <Wordmark className="auth-brand-name" />
          <span className="auth-alpha-badge t-mono">Bêta</span>
        </div>
        <a className="auth-discover" href="/decouvrir/">
          ← Découvrir Zygosight
        </a>
        <h1 className="t-h1" id="auth-pitch-title">
          Mesurez l’adoption. Sachez pourquoi elle bouge.
        </h1>
        <p className="auth-pitch-lede">
          Zygosight mesure l’expérience et l’adoption de vos logiciels métier, et montre pourquoi les chiffres
          bougent.
        </p>
        <ul className="auth-points">
          {POINTS.map((p) => (
            <li key={p.title}>
              <span className={`auth-point-icon hue-${p.hue}`}>
                <Icon name={p.icon} size={17} />
              </span>
              <span>
                <strong>{p.title}</strong>
                <span>{p.text}</span>
              </span>
            </li>
          ))}
        </ul>
        <ResearchIllustration />
      </section>

      <div className="auth-card">
        {requested ? (
          <div className="auth-requested" role="status">
            <span className="auth-point-icon hue-green">
              <Icon name="audit" size={18} />
            </span>
            <h2 className="t-h2 auth-title">Demande envoyée</h2>
            <p className="auth-lede">
              Votre compte pour « {organizationName} » est créé, mais pas encore ouvert : chaque demande est validée à
              la main. Vous pourrez vous connecter avec <strong>{email}</strong> dès qu’elle sera approuvée.
            </p>
            <Button
              variant="ghost"
              onClick={() => {
                setRequested(false);
                switchMode('login');
              }}
            >
              Retour à la connexion
            </Button>
          </div>
        ) : (
          <>
            <h2 className="t-h2 auth-title">
              {isDemo ? 'Essayer la démo' : isRegister ? 'Demander un compte' : 'Se connecter'}
            </h2>
            <p className="auth-lede">
              {isDemo
                ? 'Votre espace s’ouvre tout de suite, avec un projet exemple déjà rempli : études, verbatims, insights, mesures. Une visite guidée vous montre l’interface. Vos données restent hébergées dans l’Union européenne.'
                : isRegister
                  ? 'Votre espace sera isolé : personne d’autre n’y accède, et vos données restent hébergées dans l’Union européenne. Chaque demande est validée à la main avant l’ouverture du compte.'
                  : 'Vos mesures, vos études et vos insights.'}
            </p>

            <form className="auth-form" onSubmit={submit} noValidate>
              {isRegister ? (
                <>
                  <Field
                    label="Votre nom"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                    required
                  />
                  <Field
                    label={isDemo ? 'Nom de votre structure (facultatif)' : 'Nom de l’organisation'}
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    autoComplete="organization"
                    required={!isDemo}
                  />
                </>
              ) : null}

              <Field
                label="E-mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />

              <Field
                label="Mot de passe"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={isRegister ? 'new-password' : 'current-password'}
                {...(isRegister ? { hint: `${MIN_PASSWORD} caractères minimum` } : {})}
                {...(passwordTooShort
                  ? { error: `Encore ${MIN_PASSWORD - password.length} caractère(s).` }
                  : {})}
                required
              />

              {isRegister ? <TermsConsent checked={acceptTerms} onChange={setAcceptTerms} /> : null}

              {error ? <ErrorBanner>{error}</ErrorBanner> : null}

              <Button type="submit" variant="accent" loading={busy} disabled={!canSubmit}>
                {isDemo ? 'Entrer dans la démo' : isRegister ? 'Envoyer ma demande' : 'Entrer'}
              </Button>
            </form>

            <p className="auth-switch">
              {isRegister ? (
                <>
                  Vous avez déjà un compte ?{' '}
                  <Button variant="link" size="s" type="button" onClick={() => switchMode('login')}>
                    Se connecter
                  </Button>
                </>
              ) : (
                <>
                  Pas encore de compte ?{' '}
                  <Button variant="link" size="s" type="button" onClick={() => switchMode('demo')}>
                    Essayer la démo
                  </Button>
                </>
              )}
            </p>
          </>
        )}

        <p className="auth-notice">
          Zygosight est en bêta publique : le service évolue encore. Gardez vos propres copies de ce qui compte —
          enregistrements originaux, livrables.
        </p>
        <p className="auth-contact">
          Une question ? <a href="mailto:hello@fredericrossi.com">Contactez-moi</a>
        </p>
      </div>
    </main>
    {/* Les liens légaux : hors du bloc de connexion, en pied de page. */}
    <LegalFooter compact />
    </div>
  );
}
